import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import BackspaceIcon from '@mui/icons-material/Backspace';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { toast } from 'react-toastify';
import {
  createPhoneContact,
  deletePhoneContact,
  listPhoneContacts,
  listPhoneRecents,
  listSipTargets,
  resolveSipTarget,
  updatePhoneContact,
  validateDial,
} from '../../services/phone/phoneApi';
import {
  applyVoiceAudioDevices,
  getActiveCallSnapshot,
  hangupCall,
  isCallInProgress,
  isVoiceDeviceReady,
  sendDtmf,
  startPstnCall,
  startSipCall,
  toggleMute,
} from '../../services/voice/voiceCallService';
import { isPhoneEnabledForUser } from '../../utils/phoneEntitlement';
import {
  listBrowserAudioDevices,
  loadPhoneAudioSettings,
  requestAudioPermission,
  savePhoneAudioSettings,
} from '../../utils/browserAudioDevices';

const DIAL_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

function formatDuration(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function callStateLabel(state) {
  switch (state) {
    case 'outbound-ringing': return 'Calling…';
    case 'ringing': return 'Ringing…';
    case 'dialing':
    case 'active':
    case 'connected': return 'Connected';
    case 'busy': return 'Busy';
    case 'no-answer': return 'No Answer';
    case 'failed':
    case 'connection_failed': return 'Could not connect';
    case 'rejected': return 'Declined';
    case 'missed': return 'Missed';
    case 'ended': return 'Ended';
    default: return '';
  }
}

export default function PhoneOverlay({ open, onClose, userData, role }) {
  const [tab, setTab] = useState(0);
  const [digits, setDigits] = useState('');
  const [callState, setCallState] = useState('idle');
  const [activeNumber, setActiveNumber] = useState('');
  const [activeName, setActiveName] = useState('');
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [dialing, setDialing] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [recents, setRecents] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [micDeviceId, setMicDeviceId] = useState(() => loadPhoneAudioSettings().micDeviceId);
  const [speakerDeviceId, setSpeakerDeviceId] = useState(() => loadPhoneAudioSettings().speakerDeviceId);
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [dialMode, setDialMode] = useState('phone');
  const [sipTargets, setSipTargets] = useState([]);
  const [selectedSip, setSelectedSip] = useState(null);
  const [voiceReady, setVoiceReady] = useState(() => isVoiceDeviceReady());
  const timerRef = useRef(null);
  const entitled = isPhoneEnabledForUser(userData, role);
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const refreshLists = useCallback(async () => {
    if (!entitled) return;
    setLoadingList(true);
    try {
      const [c, r, targets] = await Promise.all([
        listPhoneContacts(),
        listPhoneRecents(),
        listSipTargets().catch((e) => {
          console.error('[PhoneOverlay] SIP target load failed', e);
          return [];
        }),
      ]);
      setContacts(c);
      setRecents(r);
      setSipTargets(targets);
    } catch (e) {
      console.error('[PhoneOverlay] list load failed', e);
    } finally {
      setLoadingList(false);
    }
  }, [entitled]);

  const refreshAudioDevices = useCallback(async () => {
    setAudioLoading(true);
    try {
      await requestAudioPermission();
      const { inputs, outputs } = await listBrowserAudioDevices();
      setAudioInputs(inputs);
      setAudioOutputs(outputs);
    } catch (e) {
      console.warn('[PhoneOverlay] audio device list failed', e);
    } finally {
      setAudioLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const sync = () => setVoiceReady(isVoiceDeviceReady());
    sync();
    const id = window.setInterval(sync, 1000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const snap = getActiveCallSnapshot();
    if (snap.state) setCallState(snap.state);
    const saved = loadPhoneAudioSettings();
    setMicDeviceId(saved.micDeviceId);
    setSpeakerDeviceId(saved.speakerDeviceId);
    refreshLists();
    refreshAudioDevices();
    const media = navigator.mediaDevices;
    if (media?.addEventListener) {
      media.addEventListener('devicechange', refreshAudioDevices);
    }
    return () => {
      media?.removeEventListener?.('devicechange', refreshAudioDevices);
    };
  }, [open, refreshLists, refreshAudioDevices]);

  useEffect(() => {
    const onActive = (event) => {
      const detail = event?.detail || {};
      setCallState('connected');
      if (detail.to || detail.phone) setActiveNumber(detail.to || detail.phone);
      if (detail.displayName) setActiveName(detail.displayName);
      if (timerRef.current) clearInterval(timerRef.current);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    };
    const onEnded = (event) => {
      const endReason = event?.detail?.endReason;
      setCallState(endReason || 'ended');
      setDialing(false);
      setMuted(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      refreshLists();
    };
    const onError = (event) => {
      setCallState('failed');
      setDialing(false);
      toast.error(event?.detail?.error || 'Call failed');
    };

    window.addEventListener('voice-call-active', onActive);
    window.addEventListener('voice-call-ended', onEnded);
    window.addEventListener('voice-outbound-error', onError);
    return () => {
      window.removeEventListener('voice-call-active', onActive);
      window.removeEventListener('voice-call-ended', onEnded);
      window.removeEventListener('voice-outbound-error', onError);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [refreshLists]);

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return contacts;
    return contacts.filter((c) =>
      (c.name || '').toLowerCase().includes(term)
      || (c.phone || '').includes(term)
      || (c.label || '').toLowerCase().includes(term));
  }, [contacts, search]);

  const directoryPhones = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => {
      const digitsOnly = String(c.phone || '').replace(/\D/g, '');
      if (digitsOnly) set.add(digitsOnly);
    });
    return set;
  }, [contacts]);

  const isInDirectory = (phone) => directoryPhones.has(String(phone || '').replace(/\D/g, ''));

  const pressKey = (key) => {
    if (callState === 'connected' || callState === 'active' || callState === 'dialing') {
      sendDtmf(key);
      return;
    }
    setSelectedSip(null);
    setDigits((prev) => prev + key);
  };

  const handleBackspace = () => {
    setSelectedSip(null);
    setDigits((prev) => prev.slice(0, -1));
  };
  const handleClear = () => {
    setSelectedSip(null);
    setDigits('');
  };

  const placeCall = async (rawNumber, displayName = '') => {
    if (!entitled) {
      toast.error('Browser phone is not enabled for your account.');
      return;
    }
    if (dialing || isCallInProgress()) {
      toast.warn('A call is already in progress.');
      return;
    }

    const target = String(rawNumber || digits || '').trim();
    if (!target) {
      toast.error('Enter a phone number.');
      return;
    }

    setDialing(true);
    setCallState('outbound-ringing');
    setActiveNumber(target);
    setActiveName(displayName);

    try {
      const validated = await validateDial(target);
      const e164 = validated.e164;
      const name = validated.displayName || displayName;
      setActiveNumber(e164);
      setActiveName(name);
      await startPstnCall({ to: e164, displayName: name });
    } catch (e) {
      console.error('[PhoneOverlay] call failed', e);
      setCallState('failed');
      setDialing(false);
      if (e?.detail === 'emergency_not_supported') {
        toast.error('This browser phone is not an emergency telephone.');
      } else {
        toast.error(e?.message || 'Unable to place call');
      }
    }
  };

  const placeSipCall = async () => {
    if (!entitled) {
      toast.error('Browser phone is not enabled for your account.');
      return;
    }
    if (dialing || isCallInProgress()) {
      toast.warn('A call is already in progress.');
      return;
    }
    const typed = String(digits || '').trim();
    const typedUri = /^sips?:/i.test(typed) || typed.includes('@')
      ? (/^sips?:/i.test(typed) ? typed : `sip:${typed}`)
      : '';

    setDialing(true);
    setCallState('outbound-ringing');
    try {
      if (typedUri) {
        if (/^sips?:[^@]*:/i.test(typedUri)) {
          throw new Error('SIP addresses with a password are not accepted.');
        }
        setSelectedSip(null);
        setActiveName(typedUri);
        setActiveNumber(typedUri);
        await startSipCall({ sipUri: typedUri });
        return;
      }
      let target = selectedSip;
      if (!target?.targetId) {
        if (!typed) {
          throw new Error('Enter an extension or select a SIP device.');
        }
        target = await resolveSipTarget(typed);
      }
      if (!target?.targetId || /^sips?:/i.test(String(target.targetId))) {
        throw new Error('Select a SIP device from your tenant.');
      }
      setSelectedSip(target);
      setActiveName(target.displayName || '');
      setActiveNumber(target.extension || '');
      await startSipCall({ targetId: target.targetId });
    } catch (e) {
      console.error('[PhoneOverlay] SIP call failed', e);
      setCallState('failed');
      setDialing(false);
      toast.error(e?.message || 'Unable to place SIP call');
    }
  };

  const handleHangup = () => {
    hangupCall();
    setDialing(false);
    setCallState('ended');
  };

  const handleMute = () => {
    const next = toggleMute();
    setMuted(next);
  };

  const persistAudioChoice = async (patch) => {
    const next = savePhoneAudioSettings(patch);
    setMicDeviceId(next.micDeviceId);
    setSpeakerDeviceId(next.speakerDeviceId);
    try {
      await applyVoiceAudioDevices(next);
    } catch (e) {
      console.warn('[PhoneOverlay] apply audio devices failed', e);
    }
  };

  const openAddContact = (phone = '', name = '') => {
    setNewContactName(name);
    setNewContactPhone(phone);
    setAddingContact(true);
    setTab(2);
  };

  const cancelAddContact = () => {
    setAddingContact(false);
    setNewContactName('');
    setNewContactPhone('');
    setSavingContact(false);
  };

  const submitAddContact = async () => {
    const name = newContactName.trim();
    const phone = newContactPhone.trim();
    if (!name) {
      toast.error('Enter a contact name.');
      return;
    }
    if (!phone) {
      toast.error('Enter a phone number.');
      return;
    }
    setSavingContact(true);
    try {
      await createPhoneContact({ name, phone, label: 'Other' });
      toast.success('Contact saved');
      cancelAddContact();
      refreshLists();
    } catch (e) {
      toast.error(e?.message || 'Failed to add contact');
      setSavingContact(false);
    }
  };

  const inCall = ['outbound-ringing', 'ringing', 'dialing', 'active', 'connected'].includes(callState);
  const phoneTitle = voiceReady ? 'Phone' : 'Phone - Not ready';
  const statusLabel = callStateLabel(callState);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={compact}
      PaperProps={{ sx: { maxHeight: compact ? '100%' : 'min(680px, 92vh)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{phoneTitle}</Typography>
        <IconButton aria-label="Close phone" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {!entitled ? (
          <Typography color="error" sx={{ py: 2 }}>
            Browser phone is not enabled for your account. Contact your administrator.
          </Typography>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 40, mb: 0.5 }}>
              <Tab label="Dial" sx={{ minHeight: 40, py: 0.5 }} />
              <Tab label="Recents" sx={{ minHeight: 40, py: 0.5 }} />
              <Tab label="Directory" sx={{ minHeight: 40, py: 0.5 }} />
              <Tab label="Settings" sx={{ minHeight: 40, py: 0.5 }} />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={compact ? 1 : 1.25}>
                {tab === 0 && !inCall && (
                  <Tabs
                    value={dialMode}
                    onChange={(_, v) => {
                      setDialMode(v);
                      if (v === 'phone') setSelectedSip(null);
                    }}
                    variant="fullWidth"
                    sx={{ minHeight: 36 }}
                  >
                    <Tab value="phone" label="Phone" sx={{ minHeight: 36, py: 0.25 }} />
                    <Tab value="sip" label="SIP" sx={{ minHeight: 36, py: 0.25 }} />
                  </Tabs>
                )}

                <Box sx={{ textAlign: 'center' }}>
                  {statusLabel && (
                    <Typography variant="caption" color="text.secondary">{statusLabel}</Typography>
                  )}
                  <Typography variant="subtitle1" sx={{ wordBreak: 'break-all', lineHeight: 1.3 }}>
                    {inCall
                      ? (activeName || activeNumber || 'Calling')
                      : (digits || (dialMode === 'sip' ? 'SIP address or extension' : 'Enter a number'))}
                  </Typography>
                  {inCall && duration > 0 && (
                    <Typography variant="body2">{formatDuration(duration)}</Typography>
                  )}
                </Box>

                {!inCall && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      label={dialMode === 'sip' ? 'SIP address, extension, or username' : 'Number'}
                      value={digits}
                      onChange={(e) => {
                        setSelectedSip(null);
                        setDigits(e.target.value);
                      }}
                      placeholder={dialMode === 'sip' ? 'sip:user@domain' : ''}
                      inputProps={{ inputMode: dialMode === 'sip' ? 'text' : 'tel' }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton aria-label="Back" onClick={handleBackspace} size="small">
                      <BackspaceIcon />
                    </IconButton>
                    <IconButton aria-label="Clear" onClick={handleClear} size="small">
                      <ClearIcon />
                    </IconButton>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CallIcon />}
                      disabled={dialing || !voiceReady}
                      onClick={() => (dialMode === 'sip' ? placeSipCall() : placeCall(digits))}
                      sx={{ height: 40, flexShrink: 0, px: 1.5 }}
                    >
                      Call
                    </Button>
                  </Stack>
                )}

                {dialMode === 'sip' && !inCall && (
                  <Stack spacing={0.25} sx={{ maxHeight: compact ? 96 : 140, overflow: 'auto' }}>
                    {sipTargets.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No active SIP devices for your tenant.</Typography>
                    )}
                    {sipTargets
                      .filter((target) => {
                        const term = digits.trim().toLowerCase();
                        if (term.includes('@') || /^sips?:/i.test(term)) return false;
                        if (!term || selectedSip) return true;
                        return (target.displayName || '').toLowerCase().includes(term)
                          || (target.extension || '').toLowerCase().includes(term);
                      })
                      .map((target) => {
                        const rawName = String(target.displayName || '').trim();
                        const shownName = !rawName || /password/i.test(rawName) ? (target.extension || rawName) : rawName;
                        return (
                          <Button
                            key={target.targetId}
                            size="small"
                            variant={selectedSip?.targetId === target.targetId ? 'contained' : 'text'}
                            onClick={() => {
                              setSelectedSip(target);
                              setDigits(target.extension || '');
                            }}
                            sx={{ justifyContent: 'space-between', py: 0.25 }}
                          >
                            <span>{shownName}</span>
                            {shownName !== target.extension && <span>{target.extension}</span>}
                          </Button>
                        );
                      })}
                  </Stack>
                )}

                {(dialMode === 'phone' || inCall) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                    {DIAL_KEYS.flat().map((key) => (
                      <Button key={key} variant="outlined" onClick={() => pressKey(key)} sx={{ py: compact ? 0.5 : 1, fontSize: compact ? '1rem' : '1.15rem', minWidth: 0 }}>
                        {key}
                      </Button>
                    ))}
                  </Box>
                )}

                {inCall && (
                  <Stack direction="row" spacing={1}>
                    <Button fullWidth startIcon={muted ? <MicOffIcon /> : <MicIcon />} onClick={handleMute}>
                      {muted ? 'Unmute' : 'Mute'}
                    </Button>
                    <Button fullWidth variant="contained" color="error" startIcon={<CallEndIcon />} onClick={handleHangup}>
                      Hang Up
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={1}>
                {loadingList && <Typography variant="body2">Loading…</Typography>}
                {!loadingList && recents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No recent calls.</Typography>
                )}
                {recents.map((r) => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{r.name || r.phone}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {r.phone} · {r.outcome} · {r.startedAt ? new Date(r.startedAt).toLocaleString() : ''}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => { setDialMode('phone'); setSelectedSip(null); setTab(0); setDigits(r.phone); placeCall(r.phone, r.name); }}>Call</Button>
                    {isInDirectory(r.phone) ? (
                      <Button size="small" disabled>Saved</Button>
                    ) : (
                      <Button size="small" onClick={() => openAddContact(r.phone, r.name || '')}>
                        Add
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={1}>
                <TextField size="small" placeholder="Search directory" value={search} onChange={(e) => setSearch(e.target.value)} />
                {!addingContact ? (
                  <Button variant="outlined" onClick={() => openAddContact()}>
                    Add Contact
                  </Button>
                ) : (
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>New contact</Typography>
                    <Stack spacing={1.5}>
                      <TextField
                        size="small"
                        label="Name"
                        autoFocus
                        value={newContactName}
                        onChange={(e) => setNewContactName(e.target.value)}
                      />
                      <TextField
                        size="small"
                        label="Phone number"
                        value={newContactPhone}
                        onChange={(e) => setNewContactPhone(e.target.value)}
                        inputProps={{ inputMode: 'tel' }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button onClick={cancelAddContact} disabled={savingContact}>Cancel</Button>
                        <Button
                          variant="contained"
                          onClick={submitAddContact}
                          disabled={savingContact}
                        >
                          {savingContact ? 'Saving…' : 'Save contact'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                )}
                {filteredContacts.map((c) => (
                  <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <IconButton
                      size="small"
                      aria-label={c.favorite ? 'Unfavorite' : 'Favorite'}
                      onClick={async () => {
                        try {
                          await updatePhoneContact(c.id, { favorite: !c.favorite });
                          refreshLists();
                        } catch (e) {
                          toast.error(e.message || 'Update failed');
                        }
                      }}
                    >
                      {c.favorite ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{c.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.label} · {c.phone}</Typography>
                      {pendingDeleteId === c.id && (
                        <Typography variant="caption" color="error" display="block">Delete this contact?</Typography>
                      )}
                    </Box>
                    <Button size="small" onClick={() => { setDialMode('phone'); setSelectedSip(null); setTab(0); placeCall(c.phone, c.name); }}>Call</Button>
                    {pendingDeleteId === c.id ? (
                      <>
                        <Button size="small" onClick={() => setPendingDeleteId(null)}>No</Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            try {
                              await deletePhoneContact(c.id);
                              setPendingDeleteId(null);
                              refreshLists();
                            } catch (e) {
                              toast.error(e.message || 'Delete failed');
                            }
                          }}
                        >
                          Yes
                        </Button>
                      </>
                    ) : (
                      <Button size="small" color="error" onClick={() => setPendingDeleteId(c.id)}>
                        Del
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
            )}

            {tab === 3 && (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Choose the microphone and speaker this dialer uses. These are the same local
                  audio devices paging uses, and they apply to the next call and the current call.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="phone-mic-label">Microphone</InputLabel>
                  <Select
                    labelId="phone-mic-label"
                    label="Microphone"
                    value={micDeviceId}
                    onChange={(e) => persistAudioChoice({ micDeviceId: e.target.value })}
                    renderValue={(val) => {
                      if (!val) return 'Default microphone';
                      const found = audioInputs.find((d) => d.deviceId === val);
                      return found?.label || 'Selected microphone';
                    }}
                  >
                    <MenuItem value="">Default microphone</MenuItem>
                    {micDeviceId && !audioInputs.some((d) => d.deviceId === micDeviceId) && (
                      <MenuItem value={micDeviceId}>Saved microphone</MenuItem>
                    )}
                    {audioInputs.map((d) => (
                      <MenuItem key={d.deviceId} value={d.deviceId}>
                        {d.label || 'Microphone'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="phone-speaker-label">Speaker</InputLabel>
                  <Select
                    labelId="phone-speaker-label"
                    label="Speaker"
                    value={speakerDeviceId}
                    onChange={(e) => persistAudioChoice({ speakerDeviceId: e.target.value })}
                    renderValue={(val) => {
                      if (!val) return 'Default speaker';
                      const found = audioOutputs.find((d) => d.deviceId === val);
                      return found?.label || 'Selected speaker';
                    }}
                  >
                    <MenuItem value="">Default speaker</MenuItem>
                    {speakerDeviceId && !audioOutputs.some((d) => d.deviceId === speakerDeviceId) && (
                      <MenuItem value={speakerDeviceId}>Saved speaker</MenuItem>
                    )}
                    {audioOutputs.map((d) => (
                      <MenuItem key={d.deviceId} value={d.deviceId}>
                        {d.label || 'Speaker'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {audioLoading && (
                  <Typography variant="caption" color="text.secondary">Loading audio devices…</Typography>
                )}
                {!audioLoading && audioInputs.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No microphones found. Allow microphone access and refresh this list.
                  </Typography>
                )}
                {!audioLoading && audioOutputs.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    This browser does not expose speaker selection. Incoming audio will use the default output.
                  </Typography>
                )}
                <Button variant="outlined" onClick={refreshAudioDevices}>Refresh devices</Button>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
